import { Helmet } from "react-helmet-async";
import { useEffect, useState } from "react";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import { Layout } from "@/components/layout/Layout";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { format } from "date-fns";
import { Mail, Phone, Calendar, LogOut, RefreshCw, Shield, DollarSign, Users, FileText, Image, Tag, Sparkles, FolderOpen, Bot, MessageSquare, Settings, Inbox, Trash2, BarChart3, Wallet, UserCog } from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { motion } from "framer-motion";
import { PricingManager } from "@/components/admin/PricingManager";
import { BlogManager } from "@/components/admin/BlogManager";
import { GalleryManager } from "@/components/admin/GalleryManager";
import { TagManager } from "@/components/admin/TagManager";
import { PortfolioManager } from "@/components/admin/PortfolioManager";
import { AIKnowledgeManager } from "@/components/admin/AIKnowledgeManager";
import { ChatHistoryManager } from "@/components/admin/ChatHistoryManager";
import BusinessSettingsManager from "@/components/admin/BusinessSettingsManager";
import { EmailInbox } from "@/components/admin/email/EmailInbox";
import { PayrollManager } from "@/components/admin/PayrollManager";
import { UserManager } from "@/components/admin/UserManager";

interface Inquiry {
  id: string;
  first_name: string;
  last_name: string;
  email: string;
  phone: string | null;
  wedding_date: string | null;
  venue: string | null;
  services: string[] | null;
  message: string | null;
  status: string;
  created_at: string;
  selected_package: string | null;
}

const statusColors: Record<string, string> = {
  new: "bg-blue-100 text-blue-800",
  contacted: "bg-yellow-100 text-yellow-800",
  booked: "bg-green-100 text-green-800",
  closed: "bg-gray-100 text-gray-800",
};

const Admin = () => {
  const { user, isAdmin, isEditor, isLoading: authLoading, signOut } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [inquiries, setInquiries] = useState<Inquiry[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedInquiry, setSelectedInquiry] = useState<Inquiry | null>(null);
  const [unreadEmailCount, setUnreadEmailCount] = useState(0);
  
  // Get initial tab from URL or default based on role
  const getDefaultTab = () => {
    const tabFromUrl = searchParams.get("tab");
    if (tabFromUrl) return tabFromUrl;
    return isEditor && !isAdmin ? "galleries" : "inquiries";
  };
  
  const [activeTab, setActiveTab] = useState(getDefaultTab());
  const [galleryStats, setGalleryStats] = useState({
    last7Days: 0,
    last14Days: 0,
    last30Days: 0,
    last90Days: 0,
    lastYear: 0,
  });
  const [blogStats, setBlogStats] = useState({
    last7Days: 0,
    last14Days: 0,
    last30Days: 0,
    last90Days: 0,
    lastYear: 0,
  });
  const [inquiryStats, setInquiryStats] = useState({
    last7Days: { total: 0, booked: 0 },
    last14Days: { total: 0, booked: 0 },
    last30Days: { total: 0, booked: 0 },
    last90Days: { total: 0, booked: 0 },
    lastYear: { total: 0, booked: 0 },
  });
  const [navigateToEmail, setNavigateToEmail] = useState<{
    email: string;
    inquiry: Inquiry;
  } | null>(null);
  const navigate = useNavigate();
  const { toast } = useToast();

  // Handle clicking on email in Inquiries tab
  const handleEmailClick = (inquiry: Inquiry) => {
    setNavigateToEmail({ email: inquiry.email, inquiry });
    setActiveTab("communication");
  };

  // Fetch total unread email count
  const fetchUnreadCount = async () => {
    try {
      const { data, error } = await supabase
        .from("email_threads")
        .select("unread_count");
      
      if (error) throw error;
      
      const total = (data || []).reduce((sum, thread) => sum + (thread.unread_count || 0), 0);
      setUnreadEmailCount(total);
    } catch (error) {
      console.error("Error fetching unread count:", error);
    }
  };

  // Fetch gallery posting statistics
  const fetchGalleryStats = async () => {
    try {
      const now = new Date();
      
      // Calculate date thresholds
      const days7Ago = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000).toISOString();
      const days14Ago = new Date(now.getTime() - 14 * 24 * 60 * 60 * 1000).toISOString();
      const days30Ago = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000).toISOString();
      const days90Ago = new Date(now.getTime() - 90 * 24 * 60 * 60 * 1000).toISOString();
      const yearAgo = new Date(now.getTime() - 365 * 24 * 60 * 60 * 1000).toISOString();
      
      // Fetch all galleries created in the last year
      const { data, error } = await supabase
        .from("galleries")
        .select("created_at")
        .gte("created_at", yearAgo);
      
      if (error) throw error;
      
      const galleries = data || [];
      
      setGalleryStats({
        last7Days: galleries.filter(g => g.created_at >= days7Ago).length,
        last14Days: galleries.filter(g => g.created_at >= days14Ago).length,
        last30Days: galleries.filter(g => g.created_at >= days30Ago).length,
        last90Days: galleries.filter(g => g.created_at >= days90Ago).length,
        lastYear: galleries.length,
      });
    } catch (error) {
      console.error("Error fetching gallery stats:", error);
    }
  };

  // Fetch blog posting statistics
  const fetchBlogStats = async () => {
    try {
      const now = new Date();
      
      // Calculate date thresholds
      const days7Ago = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000).toISOString();
      const days14Ago = new Date(now.getTime() - 14 * 24 * 60 * 60 * 1000).toISOString();
      const days30Ago = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000).toISOString();
      const days90Ago = new Date(now.getTime() - 90 * 24 * 60 * 60 * 1000).toISOString();
      const yearAgo = new Date(now.getTime() - 365 * 24 * 60 * 60 * 1000).toISOString();
      
      // Fetch all blog posts created in the last year
      const { data, error } = await supabase
        .from("blog_posts")
        .select("created_at")
        .gte("created_at", yearAgo);
      
      if (error) throw error;
      
      const posts = data || [];
      
      setBlogStats({
        last7Days: posts.filter(p => p.created_at >= days7Ago).length,
        last14Days: posts.filter(p => p.created_at >= days14Ago).length,
        last30Days: posts.filter(p => p.created_at >= days30Ago).length,
        last90Days: posts.filter(p => p.created_at >= days90Ago).length,
        lastYear: posts.length,
      });
    } catch (error) {
      console.error("Error fetching blog stats:", error);
    }
  };

  // Fetch inquiry statistics with conversion rates
  const fetchInquiryStats = async () => {
    try {
      const now = new Date();
      
      // Calculate date thresholds
      const days7Ago = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000).toISOString();
      const days14Ago = new Date(now.getTime() - 14 * 24 * 60 * 60 * 1000).toISOString();
      const days30Ago = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000).toISOString();
      const days90Ago = new Date(now.getTime() - 90 * 24 * 60 * 60 * 1000).toISOString();
      const yearAgo = new Date(now.getTime() - 365 * 24 * 60 * 60 * 1000).toISOString();
      
      // Fetch all inquiries created in the last year with status
      const { data, error } = await supabase
        .from("inquiries")
        .select("created_at, status")
        .gte("created_at", yearAgo);
      
      if (error) throw error;
      
      const allInquiries = data || [];
      
      const calculateStats = (startDate: string) => {
        const filtered = allInquiries.filter(i => i.created_at >= startDate);
        return {
          total: filtered.length,
          booked: filtered.filter(i => i.status === "booked").length,
        };
      };
      
      setInquiryStats({
        last7Days: calculateStats(days7Ago),
        last14Days: calculateStats(days14Ago),
        last30Days: calculateStats(days30Ago),
        last90Days: calculateStats(days90Ago),
        lastYear: calculateStats(yearAgo),
      });
    } catch (error) {
      console.error("Error fetching inquiry stats:", error);
    }
  };

  useEffect(() => {
    if (!authLoading && !user) {
      navigate("/auth");
    }
  }, [authLoading, user, navigate]);

  // Set default tab based on role and sync URL params
  useEffect(() => {
    if (isEditor && !isAdmin && activeTab === "inquiries") {
      setActiveTab("galleries");
    }
  }, [isEditor, isAdmin, activeTab]);

  // Read tab from URL on mount or when URL changes
  useEffect(() => {
    const tabFromUrl = searchParams.get("tab");
    if (tabFromUrl && tabFromUrl !== activeTab) {
      setActiveTab(tabFromUrl);
    }
  }, [searchParams]);

  useEffect(() => {
    const hasAccess = isAdmin || isEditor;
    if (user && hasAccess) {
      // Only fetch admin-only data if admin
      if (isAdmin) {
        fetchInquiries();
        fetchUnreadCount();
        fetchInquiryStats();
      }
      
      // Fetch data available to both admins and editors
      fetchGalleryStats();
      fetchBlogStats();

      // Subscribe to real-time updates on email_threads (admin only)
      if (isAdmin) {
        const channel = supabase
          .channel('email-threads-changes')
          .on(
            'postgres_changes',
            {
              event: '*',
              schema: 'public',
              table: 'email_threads'
            },
            (payload) => {
              console.log('Email thread update:', payload);
              fetchUnreadCount();
              
              // Show toast for new inbound emails (INSERT or UPDATE with increased unread)
              if (payload.eventType === 'INSERT' || 
                  (payload.eventType === 'UPDATE' && 
                   (payload.new as any).unread_count > ((payload.old as any)?.unread_count || 0))) {
                toast({
                  title: "New email received",
                  description: `You have a new message in: ${(payload.new as any).subject}`,
                });
              }
            }
          )
          .subscribe();

        return () => {
          supabase.removeChannel(channel);
        };
      }
    }
  }, [user, isAdmin, isEditor]);

  const fetchInquiries = async () => {
    setIsLoading(true);
    try {
      const { data, error } = await supabase
        .from("inquiries")
        .select("*")
        .order("created_at", { ascending: false });

      if (error) throw error;
      setInquiries(data || []);
    } catch (error: any) {
      console.error("Error fetching inquiries:", error);
      toast({
        title: "Error loading inquiries",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const updateStatus = async (id: string, status: string) => {
    try {
      const { error } = await supabase
        .from("inquiries")
        .update({ status })
        .eq("id", id);

      if (error) throw error;

      setInquiries((prev) =>
        prev.map((inq) => (inq.id === id ? { ...inq, status } : inq))
      );

      toast({ title: "Status updated" });
    } catch (error: any) {
      toast({
        title: "Error updating status",
        description: error.message,
        variant: "destructive",
      });
    }
  };

  const deleteInquiry = async (id: string) => {
    try {
      const { error } = await supabase
        .from("inquiries")
        .delete()
        .eq("id", id);

      if (error) throw error;

      setInquiries((prev) => prev.filter((inq) => inq.id !== id));

      toast({ title: "Inquiry deleted" });
    } catch (error: any) {
      toast({
        title: "Error deleting inquiry",
        description: error.message,
        variant: "destructive",
      });
    }
  };

  const handleSignOut = async () => {
    await signOut();
    navigate("/");
  };

  if (authLoading) {
    return (
      <Layout>
        <div className="flex min-h-[60vh] items-center justify-center">
          <div className="text-center">
            <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent mx-auto" />
            <p className="mt-4 text-muted-foreground">Loading...</p>
          </div>
        </div>
      </Layout>
    );
  }

  if (!user) {
    return null;
  }

  // Check if user has any admin-like access
  const hasAccess = isAdmin || isEditor;

  if (!hasAccess) {
    return (
      <>
        <Helmet>
          <title>Access Denied - Pixie Clicks</title>
        </Helmet>
        <Layout>
          <section className="section-padding">
            <div className="container-narrow text-center">
              <Shield className="mx-auto h-16 w-16 text-muted-foreground" />
              <h1 className="mt-6 font-serif text-3xl font-semibold text-foreground">
                Access Denied
              </h1>
              <p className="mt-4 text-muted-foreground">
                You don't have privileges to access this page.
              </p>
              <div className="mt-8 flex justify-center gap-4">
                <Button variant="hero-outline" onClick={handleSignOut}>
                  Sign Out
                </Button>
                <Button variant="hero" asChild>
                  <Link to="/">Go Home</Link>
                </Button>
              </div>
            </div>
          </section>
        </Layout>
      </>
    );
  }

  return (
    <>
      <Helmet>
        <title>Admin Dashboard - Pixie Clicks</title>
      </Helmet>
      <Layout>
        <section className="section-padding">
          <div className="container-wide">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
            >
              {/* Header */}
              <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                <div>
                  <h1 className="font-serif text-3xl font-semibold text-foreground">
                    Admin Dashboard
                  </h1>
                  <p className="mt-1 text-muted-foreground">
                    Manage inquiries, pricing, and more
                  </p>
                </div>
                <div className="flex gap-3">
                  <Button variant="outline" onClick={handleSignOut}>
                    <LogOut className="mr-2 h-4 w-4" />
                    Sign Out
                  </Button>
                </div>
              </div>

              {/* Tabs */}
              <Tabs value={activeTab} onValueChange={setActiveTab} className="mt-8">
                <TabsList className="grid w-full grid-cols-4 lg:w-auto lg:grid-cols-none lg:inline-flex">
                  {/* Admin-only tabs */}
                  {isAdmin && (
                    <>
                      <TabsTrigger value="inquiries" className="gap-2">
                        <Users className="h-4 w-4" />
                        <span className="hidden sm:inline">Inquiries</span>
                      </TabsTrigger>
                      <TabsTrigger value="communication" className="gap-2 relative">
                        <Inbox className="h-4 w-4" />
                        <span className="hidden sm:inline">Communication</span>
                        {unreadEmailCount > 0 && (
                          <Badge 
                            variant="destructive" 
                            className="absolute -top-2 -right-2 h-5 w-5 p-0 flex items-center justify-center text-xs animate-pulse"
                          >
                            {unreadEmailCount > 99 ? '99+' : unreadEmailCount}
                          </Badge>
                        )}
                      </TabsTrigger>
                      <TabsTrigger value="chats" className="gap-2">
                        <MessageSquare className="h-4 w-4" />
                        <span className="hidden sm:inline">Chats</span>
                      </TabsTrigger>
                    </>
                  )}
                  
                  {/* Shared tabs (Admin + Editor) */}
                  <TabsTrigger value="galleries" className="gap-2">
                    <Image className="h-4 w-4" />
                    <span className="hidden sm:inline">Galleries</span>
                  </TabsTrigger>
                  <TabsTrigger value="blog" className="gap-2">
                    <FileText className="h-4 w-4" />
                    <span className="hidden sm:inline">Blog</span>
                  </TabsTrigger>
                  <TabsTrigger value="portfolio" className="gap-2">
                    <FolderOpen className="h-4 w-4" />
                    <span className="hidden sm:inline">Portfolio</span>
                  </TabsTrigger>
                  <TabsTrigger value="stats" className="gap-2">
                    <BarChart3 className="h-4 w-4" />
                    <span className="hidden sm:inline">Stats</span>
                  </TabsTrigger>
                  <TabsTrigger value="payroll" className="gap-2">
                    <Wallet className="h-4 w-4" />
                    <span className="hidden sm:inline">Payroll</span>
                  </TabsTrigger>

                  {/* Admin-only tabs */}
                  {isAdmin && (
                    <>
                      <TabsTrigger value="users" className="gap-2">
                        <UserCog className="h-4 w-4" />
                        <span className="hidden sm:inline">Users</span>
                      </TabsTrigger>
                      <TabsTrigger value="pricing" className="gap-2">
                        <DollarSign className="h-4 w-4" />
                        <span className="hidden sm:inline">Pricing</span>
                      </TabsTrigger>
                      <TabsTrigger value="tags" className="gap-2">
                        <Tag className="h-4 w-4" />
                        <span className="hidden sm:inline">Tags</span>
                      </TabsTrigger>
                      <TabsTrigger value="ai-knowledge" className="gap-2">
                        <Bot className="h-4 w-4" />
                        <span className="hidden sm:inline">AI</span>
                      </TabsTrigger>
                      <TabsTrigger value="settings" className="gap-2">
                        <Settings className="h-4 w-4" />
                        <span className="hidden sm:inline">Settings</span>
                      </TabsTrigger>
                    </>
                  )}
                </TabsList>

                <TabsContent value="inquiries" className="mt-6">
                  {/* Stats */}
                  <div className="flex justify-end mb-4">
                    <Button
                      variant="outline"
                      onClick={fetchInquiries}
                      disabled={isLoading}
                      size="sm"
                    >
                      <RefreshCw
                        className={`mr-2 h-4 w-4 ${isLoading ? "animate-spin" : ""}`}
                      />
                      Refresh
                    </Button>
                  </div>
                  
                  <div className="grid gap-4 sm:grid-cols-4 mb-6">
                    {["new", "contacted", "booked", "closed"].map((status) => (
                      <div
                        key={status}
                        className="rounded-xl bg-card border border-border p-6"
                      >
                        <p className="text-sm font-medium capitalize text-muted-foreground">
                          {status}
                        </p>
                        <p className="mt-2 text-3xl font-bold text-foreground">
                          {inquiries.filter((i) => i.status === status).length}
                        </p>
                      </div>
                    ))}
                  </div>

                  {/* Table */}
                  <div className="rounded-xl bg-card border border-border overflow-hidden">
                    {isLoading ? (
                      <div className="flex h-64 items-center justify-center">
                        <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
                      </div>
                    ) : inquiries.length === 0 ? (
                      <div className="flex h-64 items-center justify-center text-muted-foreground">
                        No inquiries yet
                      </div>
                    ) : (
                      <div className="overflow-x-auto">
                        <Table>
                          <TableHeader>
                            <TableRow>
                              <TableHead>Date</TableHead>
                              <TableHead>Name</TableHead>
                              <TableHead>Contact</TableHead>
                              <TableHead>Wedding Date</TableHead>
                              <TableHead>Package</TableHead>
                              <TableHead>Services</TableHead>
                              <TableHead>Status</TableHead>
                              <TableHead>Actions</TableHead>
                              <TableHead>Delete</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {inquiries.map((inquiry) => (
                              <TableRow key={inquiry.id}>
                                <TableCell className="whitespace-nowrap">
                                  {format(new Date(inquiry.created_at), "MMM d, yyyy")}
                                </TableCell>
                                <TableCell>
                                  <div className="font-medium">
                                    {inquiry.first_name} {inquiry.last_name}
                                  </div>
                                </TableCell>
                                <TableCell>
                                  <div className="space-y-1">
                                                    <div className="flex items-center gap-1 text-sm">
                                                      <Mail className="h-3 w-3" />
                                                      <button
                                                        type="button"
                                                        onClick={() => handleEmailClick(inquiry)}
                                                        className="hover:text-primary hover:underline text-left"
                                                      >
                                                        {inquiry.email}
                                                      </button>
                                                    </div>
                                    {inquiry.phone && (
                                      <div className="flex items-center gap-1 text-sm text-muted-foreground">
                                        <Phone className="h-3 w-3" />
                                        {inquiry.phone}
                                      </div>
                                    )}
                                  </div>
                                </TableCell>
                                <TableCell>
                                  {inquiry.wedding_date ? (
                                    <div className="flex items-center gap-1">
                                      <Calendar className="h-3 w-3" />
                                      {format(
                                        new Date(inquiry.wedding_date),
                                        "MMM d, yyyy"
                                      )}
                                    </div>
                                  ) : (
                                    <span className="text-muted-foreground">—</span>
                                  )}
                                </TableCell>
                                <TableCell>
                                  {inquiry.selected_package ? (
                                    <Badge variant="outline" className="gap-1 border-primary/30 bg-primary/5 text-primary">
                                      <Sparkles className="h-3 w-3" />
                                      {inquiry.selected_package}
                                    </Badge>
                                  ) : (
                                    <span className="text-muted-foreground">—</span>
                                  )}
                                </TableCell>
                                <TableCell>
                                  <div className="flex flex-wrap gap-1">
                                    {inquiry.services?.map((service) => (
                                      <Badge
                                        key={service}
                                        variant="secondary"
                                        className="text-xs"
                                      >
                                        {service}
                                      </Badge>
                                    )) || (
                                      <span className="text-muted-foreground">—</span>
                                    )}
                                  </div>
                                </TableCell>
                                <TableCell>
                                  <Badge
                                    className={`capitalize ${statusColors[inquiry.status] || ""}`}
                                  >
                                    {inquiry.status}
                                  </Badge>
                                </TableCell>
                                <TableCell>
                                  <Select
                                    value={inquiry.status}
                                    onValueChange={(value) =>
                                      updateStatus(inquiry.id, value)
                                    }
                                  >
                                    <SelectTrigger className="w-32">
                                      <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                      <SelectItem value="new">New</SelectItem>
                                      <SelectItem value="contacted">Contacted</SelectItem>
                                      <SelectItem value="booked">Booked</SelectItem>
                                      <SelectItem value="closed">Closed</SelectItem>
                                    </SelectContent>
                                  </Select>
                                </TableCell>
                                <TableCell>
                                  <AlertDialog>
                                    <AlertDialogTrigger asChild>
                                      <Button variant="ghost" size="icon" className="text-destructive hover:text-destructive hover:bg-destructive/10">
                                        <Trash2 className="h-4 w-4" />
                                      </Button>
                                    </AlertDialogTrigger>
                                    <AlertDialogContent>
                                      <AlertDialogHeader>
                                        <AlertDialogTitle>Delete Inquiry</AlertDialogTitle>
                                        <AlertDialogDescription>
                                          Are you sure you want to delete the inquiry from {inquiry.first_name} {inquiry.last_name}? This action cannot be undone.
                                        </AlertDialogDescription>
                                      </AlertDialogHeader>
                                      <AlertDialogFooter>
                                        <AlertDialogCancel>Cancel</AlertDialogCancel>
                                        <AlertDialogAction
                                          onClick={() => deleteInquiry(inquiry.id)}
                                          className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                                        >
                                          Delete
                                        </AlertDialogAction>
                                      </AlertDialogFooter>
                                    </AlertDialogContent>
                                  </AlertDialog>
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </div>
                    )}
                  </div>
                </TabsContent>

                <TabsContent value="communication" className="mt-6">
                  <EmailInbox 
                    navigateToEmail={navigateToEmail}
                    onNavigationHandled={() => setNavigateToEmail(null)}
                  />
                </TabsContent>

                <TabsContent value="chats" className="mt-6">
                  <ChatHistoryManager />
                </TabsContent>

                <TabsContent value="blog" className="mt-6">
                  <BlogManager />
                </TabsContent>

                <TabsContent value="galleries" className="mt-6">
                  <GalleryManager />
                </TabsContent>

                <TabsContent value="portfolio" className="mt-6">
                  <PortfolioManager />
                </TabsContent>

                <TabsContent value="pricing" className="mt-6">
                  <PricingManager />
                </TabsContent>

                <TabsContent value="tags" className="mt-6">
                  <TagManager />
                </TabsContent>

                <TabsContent value="ai-knowledge" className="mt-6">
                  <AIKnowledgeManager />
                </TabsContent>

                <TabsContent value="stats" className="mt-6 space-y-6">
                  <Card>
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        <Image className="h-5 w-5" />
                        Gallery Posting Statistics
                      </CardTitle>
                      <CardDescription>
                        Overview of galleries created over different time periods
                      </CardDescription>
                    </CardHeader>
                    <CardContent>
                      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-5">
                        <div className="rounded-xl bg-muted/50 border border-border p-6 text-center">
                          <p className="text-sm font-medium text-muted-foreground">Last 7 Days</p>
                          <p className="mt-2 text-3xl font-bold text-foreground">{galleryStats.last7Days}</p>
                        </div>
                        <div className="rounded-xl bg-muted/50 border border-border p-6 text-center">
                          <p className="text-sm font-medium text-muted-foreground">Last 14 Days</p>
                          <p className="mt-2 text-3xl font-bold text-foreground">{galleryStats.last14Days}</p>
                        </div>
                        <div className="rounded-xl bg-muted/50 border border-border p-6 text-center">
                          <p className="text-sm font-medium text-muted-foreground">Last 30 Days</p>
                          <p className="mt-2 text-3xl font-bold text-foreground">{galleryStats.last30Days}</p>
                        </div>
                        <div className="rounded-xl bg-muted/50 border border-border p-6 text-center">
                          <p className="text-sm font-medium text-muted-foreground">Last 90 Days</p>
                          <p className="mt-2 text-3xl font-bold text-foreground">{galleryStats.last90Days}</p>
                        </div>
                        <div className="rounded-xl bg-muted/50 border border-border p-6 text-center">
                          <p className="text-sm font-medium text-muted-foreground">Last Year</p>
                          <p className="mt-2 text-3xl font-bold text-foreground">{galleryStats.lastYear}</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  <Card>
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        <FileText className="h-5 w-5" />
                        Blog Post Statistics
                      </CardTitle>
                      <CardDescription>
                        Overview of blog posts created over different time periods
                      </CardDescription>
                    </CardHeader>
                    <CardContent>
                      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-5">
                        <div className="rounded-xl bg-muted/50 border border-border p-6 text-center">
                          <p className="text-sm font-medium text-muted-foreground">Last 7 Days</p>
                          <p className="mt-2 text-3xl font-bold text-foreground">{blogStats.last7Days}</p>
                        </div>
                        <div className="rounded-xl bg-muted/50 border border-border p-6 text-center">
                          <p className="text-sm font-medium text-muted-foreground">Last 14 Days</p>
                          <p className="mt-2 text-3xl font-bold text-foreground">{blogStats.last14Days}</p>
                        </div>
                        <div className="rounded-xl bg-muted/50 border border-border p-6 text-center">
                          <p className="text-sm font-medium text-muted-foreground">Last 30 Days</p>
                          <p className="mt-2 text-3xl font-bold text-foreground">{blogStats.last30Days}</p>
                        </div>
                        <div className="rounded-xl bg-muted/50 border border-border p-6 text-center">
                          <p className="text-sm font-medium text-muted-foreground">Last 90 Days</p>
                          <p className="mt-2 text-3xl font-bold text-foreground">{blogStats.last90Days}</p>
                        </div>
                        <div className="rounded-xl bg-muted/50 border border-border p-6 text-center">
                          <p className="text-sm font-medium text-muted-foreground">Last Year</p>
                          <p className="mt-2 text-3xl font-bold text-foreground">{blogStats.lastYear}</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  <Card>
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        <Users className="h-5 w-5" />
                        Inquiry Statistics & Conversion Rates
                      </CardTitle>
                      <CardDescription>
                        Overview of inquiries received and booking conversion rates
                      </CardDescription>
                    </CardHeader>
                    <CardContent>
                      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-5">
                        <div className="rounded-xl bg-muted/50 border border-border p-6 text-center">
                          <p className="text-sm font-medium text-muted-foreground">Last 7 Days</p>
                          <p className="mt-2 text-3xl font-bold text-foreground">{inquiryStats.last7Days.total}</p>
                          <p className="mt-1 text-sm text-primary">
                            {inquiryStats.last7Days.total > 0 
                              ? `${Math.round((inquiryStats.last7Days.booked / inquiryStats.last7Days.total) * 100)}% booked`
                              : "0% booked"}
                          </p>
                        </div>
                        <div className="rounded-xl bg-muted/50 border border-border p-6 text-center">
                          <p className="text-sm font-medium text-muted-foreground">Last 14 Days</p>
                          <p className="mt-2 text-3xl font-bold text-foreground">{inquiryStats.last14Days.total}</p>
                          <p className="mt-1 text-sm text-primary">
                            {inquiryStats.last14Days.total > 0 
                              ? `${Math.round((inquiryStats.last14Days.booked / inquiryStats.last14Days.total) * 100)}% booked`
                              : "0% booked"}
                          </p>
                        </div>
                        <div className="rounded-xl bg-muted/50 border border-border p-6 text-center">
                          <p className="text-sm font-medium text-muted-foreground">Last 30 Days</p>
                          <p className="mt-2 text-3xl font-bold text-foreground">{inquiryStats.last30Days.total}</p>
                          <p className="mt-1 text-sm text-primary">
                            {inquiryStats.last30Days.total > 0 
                              ? `${Math.round((inquiryStats.last30Days.booked / inquiryStats.last30Days.total) * 100)}% booked`
                              : "0% booked"}
                          </p>
                        </div>
                        <div className="rounded-xl bg-muted/50 border border-border p-6 text-center">
                          <p className="text-sm font-medium text-muted-foreground">Last 90 Days</p>
                          <p className="mt-2 text-3xl font-bold text-foreground">{inquiryStats.last90Days.total}</p>
                          <p className="mt-1 text-sm text-primary">
                            {inquiryStats.last90Days.total > 0 
                              ? `${Math.round((inquiryStats.last90Days.booked / inquiryStats.last90Days.total) * 100)}% booked`
                              : "0% booked"}
                          </p>
                        </div>
                        <div className="rounded-xl bg-muted/50 border border-border p-6 text-center">
                          <p className="text-sm font-medium text-muted-foreground">Last Year</p>
                          <p className="mt-2 text-3xl font-bold text-foreground">{inquiryStats.lastYear.total}</p>
                          <p className="mt-1 text-sm text-primary">
                            {inquiryStats.lastYear.total > 0 
                              ? `${Math.round((inquiryStats.lastYear.booked / inquiryStats.lastYear.total) * 100)}% booked`
                              : "0% booked"}
                          </p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </TabsContent>

                <TabsContent value="settings" className="mt-6">
                  <BusinessSettingsManager />
                </TabsContent>

                <TabsContent value="payroll" className="mt-6">
                  <PayrollManager />
                </TabsContent>

                <TabsContent value="users" className="mt-6">
                  <UserManager />
                </TabsContent>
              </Tabs>
            </motion.div>
          </div>
        </section>
      </Layout>
    </>
  );
};

export default Admin;
